/**
 * The handful of platform services this package needs, without assuming a browser.
 *
 * `crypto`, `TextEncoder` and `TextDecoder` are web platform APIs. React
 * Native has no `crypto` global at all, and its text codecs depend on the
 * engine and version. A package shared with a native client cannot call them
 * directly, so each one is used when the host provides it and implemented here
 * when it does not.
 *
 * The fallbacks are exercised by tests with the globals removed. Untested
 * fallback code is how a client works everywhere except the one platform the
 * fallback existed for.
 */

interface MaybeCrypto {
  randomUUID?: () => string;
  getRandomValues?: <T extends ArrayBufferView>(array: T) => T;
}

/** Structural types, so nothing here depends on the web platform's lib. */
interface TextEncoderLike {
  encode(text: string): Uint8Array;
}
interface TextDecoderLike {
  decode(bytes: Uint8Array): string;
}

function hostCrypto(): MaybeCrypto | undefined {
  return (globalThis as { crypto?: MaybeCrypto }).crypto;
}

/** Sixteen random bytes, from the strongest source available. */
function randomBytes(): Uint8Array {
  const bytes = new Uint8Array(16);
  const source = hostCrypto();
  if (source?.getRandomValues) {
    source.getRandomValues(bytes);
    return bytes;
  }
  // Last resort. These identifiers need to be unique, not unguessable: they
  // are request correlation ids and message idempotency keys, never secrets.
  for (let index = 0; index < bytes.length; index += 1) {
    bytes[index] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

const HEX = /* @__PURE__ */ Array.from({ length: 256 }, (_, index) =>
  index.toString(16).padStart(2, '0'),
);

/**
 * A version 4 UUID.
 *
 * Used for request ids and for `message_id`, which the server treats as the
 * idempotency key for sending — so a collision would silently drop somebody's
 * message rather than deliver it twice.
 */
export function newUuid(): string {
  const native = hostCrypto()?.randomUUID;
  if (native) return native.call(hostCrypto());

  const bytes = randomBytes();
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => HEX[byte]!);
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-` +
    `${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
  );
}

/** Encode text as UTF-8. */
export function utf8Encode(text: string): Uint8Array {
  const Encoder = (globalThis as { TextEncoder?: new () => TextEncoderLike }).TextEncoder;
  if (Encoder) return new Encoder().encode(text);

  const bytes: number[] = [];
  for (let index = 0; index < text.length; index += 1) {
    let point = text.codePointAt(index)!;
    if (point > 0xffff) index += 1; // A surrogate pair is one code point.
    if (point < 0x80) {
      bytes.push(point);
    } else if (point < 0x800) {
      bytes.push(0xc0 | (point >> 6), 0x80 | (point & 0x3f));
    } else if (point < 0x10000) {
      bytes.push(0xe0 | (point >> 12), 0x80 | ((point >> 6) & 0x3f), 0x80 | (point & 0x3f));
    } else {
      bytes.push(
        0xf0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3f),
        0x80 | ((point >> 6) & 0x3f),
        0x80 | (point & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

/** Decode UTF-8 bytes back into text. */
export function utf8Decode(bytes: Uint8Array): string {
  const Decoder = (globalThis as { TextDecoder?: new () => TextDecoderLike }).TextDecoder;
  if (Decoder) return new Decoder().decode(bytes);

  let output = '';
  for (let index = 0; index < bytes.length; ) {
    const first = bytes[index]!;
    let point: number;
    let width: number;
    if (first < 0x80) {
      point = first;
      width = 1;
    } else if (first < 0xe0) {
      point = first & 0x1f;
      width = 2;
    } else if (first < 0xf0) {
      point = first & 0x0f;
      width = 3;
    } else {
      point = first & 0x07;
      width = 4;
    }
    for (let offset = 1; offset < width; offset += 1) {
      point = (point << 6) | (bytes[index + offset]! & 0x3f);
    }
    output += String.fromCodePoint(point);
    index += width;
  }
  return output;
}

/**
 * A fraction in [0, 1), from the strongest source available.
 *
 * Used for reconnect jitter, where the point is that a thousand clients that
 * lost the same server do not all come back at the same instant.
 */
export function randomFraction(): number {
  const source = hostCrypto();
  if (source?.getRandomValues) {
    const buffer = new Uint32Array(1);
    source.getRandomValues(buffer);
    return (buffer[0] ?? 0) / 0x1_0000_0000;
  }
  return Math.random();
}

/**
 * A multipart body carrying one named file.
 *
 * The three-argument `append` is what sets the filename part of the multipart
 * header, and every runtime this package targets supports it — but React
 * Native's type definitions declare only two parameters. Narrowing the call
 * through a structural type keeps the filename without pretending the DOM's
 * `FormData` is the one in use.
 */
/**
 * A file, as each platform can actually produce one.
 *
 * The browser hands over a `Blob`. React Native cannot: a photo picked from
 * the gallery is a `content://` or `file://` URI, and its `FormData` accepts
 * that descriptor directly and streams the file itself. Narrowing this to
 * `Blob` would force the phone to read whole images into JavaScript memory
 * before uploading them.
 */
export type UploadFile = Blob | { uri: string; name: string; type: string };

export function multipartWithFile(field: string, file: UploadFile, fileName: string): FormData {
  const body = new FormData() as FormData & {
    append(name: string, value: UploadFile, fileName?: string): void;
  };
  body.append(field, file, fileName);
  return body;
}

/**
 * A query string, built without `URLSearchParams`.
 *
 * The class exists on both platforms but is not the same class. React Native
 * ships a subset whose constructor understands a string, an array of pairs or
 * a plain object — and nothing else. Handing it another `URLSearchParams`,
 * which the web platform specifies as a copy, takes the "plain object" branch
 * and serialises the instance's private field: the result is a query string
 * reading `_searchParams=[object Map]`. It fails silently, on one platform
 * only, and produces a URL the server rejects.
 */
export function queryString(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const encoded = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join('&');
  return encoded ? `?${encoded}` : '';
}
